import React, { useEffect, useState } from 'react';
import './FuelPriceCard.css';

interface FuelPriceResponse {
  benzin: string | null;
  gazolaj: string | null;
}

const VEDETT_BENZIN = 595;
const VEDETT_GAZOLAJ = 615;

function formatDiff(avg: string | null, vedett: number): string | null {
  if (avg === null) return null;
  const diff = parseFloat(avg) - vedett;
  return diff.toFixed(1);
}

export default function FuelPriceCard() {
  const [data, setData] = useState<FuelPriceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE}/fuel-price`, { headers: { portal: 'origo' } })
      .then(async (res) => {
        const text = await res.text();
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);
        if (!text.trim()) return { benzin: null, gazolaj: null } as FuelPriceResponse;
        try {
          return JSON.parse(text) as FuelPriceResponse;
        } catch {
          throw new Error(`JSON parse hiba, kapott szöveg: ${text}`);
        }
      })
      .then((json: FuelPriceResponse) => {
        setData(json);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, []);

  const benzinDiff = data ? formatDiff(data.benzin, VEDETT_BENZIN) : null;
  const gazolajDiff = data ? formatDiff(data.gazolaj, VEDETT_GAZOLAJ) : null;

  const isEmbed = new URLSearchParams(globalThis.location.search).get('embed') === '1';

  return (
    <div className={`fpc-wrapper${isEmbed ? ' fpc-embed' : ''}`}>
      <div className="fpc-overlay" />
      <div className="fpc-card">
        <div className="fpc-header">
          <span className="fpc-pump-icon">⛽</span>
          <h1 className="fpc-title">
            Védett ár – <span>ennyivel olcsóbb!</span>
          </h1>
        </div>

        {loading && <div className="fpc-status">Betöltés...</div>}
        {error && <div className="fpc-status fpc-error">Nem sikerült betölteni az árakat: {error}</div>}

        {!loading && !error && (
          <div className="fpc-grid">
            {/* BAL – Átlagárak */}
            <div className="fpc-panel fpc-panel-left">
              <div className="fpc-panel-title">Átlagárak</div>
              <div className="fpc-fuel-row">
                <div className="fpc-fuel-icon fpc-green">⛽</div>
                <div className="fpc-fuel-info">
                  <span className="fpc-fuel-label">95-ös benzin</span>
                  <span className="fpc-fuel-price">
                    {data?.benzin ?? '–'} <span className="fpc-fuel-unit">Ft/l</span>
                  </span>
                </div>
              </div>
              <div className="fpc-fuel-row">
                <div className="fpc-fuel-icon fpc-yellow">⛽</div>
                <div className="fpc-fuel-info">
                  <span className="fpc-fuel-label">Gázolaj</span>
                  <span className="fpc-fuel-price">
                    {data?.gazolaj ?? '–'} <span className="fpc-fuel-unit">Ft/l</span>
                  </span>
                </div>
              </div>
            </div>

            {/* KÖZÉP – Védett árak */}
            <div className="fpc-panel fpc-panel-center">
              <div className="fpc-panel-title">Védett árak</div>
              <div className="fpc-fuel-row">
                <div className="fpc-fuel-icon fpc-green-sm">95</div>
                <div className="fpc-fuel-info">
                  <span className="fpc-center-label">Védett ár (95):</span>
                  <span className="fpc-fuel-price">
                    {VEDETT_BENZIN} <span className="fpc-fuel-unit">Ft/l</span>
                  </span>
                </div>
              </div>
              <div className="fpc-fuel-row">
                <div className="fpc-fuel-icon fpc-yellow-sm">G</div>
                <div className="fpc-fuel-info">
                  <span className="fpc-center-label">Védett ár (Gázolaj):</span>
                  <span className="fpc-fuel-price">
                    {VEDETT_GAZOLAJ} <span className="fpc-fuel-unit">Ft/l</span>
                  </span>
                </div>
              </div>
            </div>

            {/* JOBB – Eltérés */}
            <div className="fpc-panel fpc-panel-right">
              <div className="fpc-panel-title">Eltérés a védett ártól</div>
              <div className="fpc-diff-block">
                <div className="fpc-diff-value fpc-diff-green">
                  +{benzinDiff !== null ? `${benzinDiff} Ft` : '–'}
                </div>
                <div className="fpc-diff-label">95-ös benzin</div>
              </div>
              <div className="fpc-divider" />
              <div className="fpc-diff-block">
                <div className="fpc-diff-value fpc-diff-yellow">
                  +{gazolajDiff !== null ? `${gazolajDiff} Ft` : '–'}
                </div>
                <div className="fpc-diff-label">Gázolaj</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
